import React from "react";
import { ResizeSplit } from "./ResizeSplit";
import "./Profiling.scss";
import { Button, Switch, FormControlLabel } from "../../../shared/components";
import { useAppSelector, useThunkDispatch } from "../../../store";
import {
    setProfilingEnabled,
    setReactRenderEnabled,
    clearProfiling,
    selectCycle,
} from "../../../store/profiling/actions";
import type { ProfileData } from "@recursive-robot/react-jsx-parser";
import { groupSummaries, selectSelectedCycle, templateBatches } from "../../../store/profiling/selectors";
import { EMPTY_FILTERS, eventOptionKey, filterGroups, hasActiveFilters, matchingCycleIds, type EventOption, type ProfilingFilters as Filters } from "../../../store/profiling/filtering";
import { DEFAULT_SORT, type PassSort } from "../../../store/profiling/sorting";
import { buildProfilingExport } from "../../../store/profiling/exporting";
import { downloadJson } from "../../../shared/utilities";
import { isProfilingBuild, setProfilingBuildCookie } from "../../../shared/profiling-build";
import CycleTree from "./CycleTree";
import TriggeredBy from "./TriggeredBy";
import FlameGraph from "./FlameGraph";
import ProfileTimeline from "./ProfileTimeline";
import NodeDetail from "./NodeDetail";
import AggregateView from "./AggregateView";
import ProfilingFilters from "./ProfilingFilters";
import PassSortBar from "./PassSortBar";
import CompareView from "./CompareView";
import { useBatchSelection } from "./useBatchSelection";
import { type TimelineScope } from "./ProfileTimeline";
import { cycleBatches, batchKey } from "./viz";
import { format } from "date-fns";

type ViewMode = "passes" | "aggregate" | "compare";

function Profiling() {
    const dispatch = useThunkDispatch();
    const enabled = useAppSelector(s => s.profiling.enabled);
    const reactRenderEnabled = useAppSelector(s => s.profiling.reactRenderEnabled);
    const groupCount = useAppSelector(s => s.profiling.groupOrder.length);
    const selectedCycle = useAppSelector(selectSelectedCycle);
    const selectedCycleId = useAppSelector(s => s.profiling.selectedCycleId);
    const profiling = useAppSelector(s => s.profiling);
    const traces = useAppSelector(s => s.tracing.traces);

    const [viewMode, setViewMode] = React.useState<ViewMode>("passes");

    // The Compare view's A/B cycles — owned here so the tree's context menu can assign them.
    const [compare, setCompare] = React.useState<{ a: string | null; b: string | null }>({ a: null, b: null });
    const setCompareSide = React.useCallback(
        (side: "a" | "b", cycleId: string | null) => setCompare(prev => ({ ...prev, [side]: cycleId })),
        []
    );
    // Context-menu handler - once both sides are populated, open the "Comparison" view.
    const selectForComparison = React.useCallback((side: "a" | "b", cycleId: string) => {
        const next = { ...compare, [side]: cycleId };
        setCompare(next);
        if (next.a && next.b) setViewMode("compare");
    }, [compare]);

    // Filters apply to both views (and survive toggling between them): OR within a filter, AND across.
    const [filters, setFilters] = React.useState<Filters>(EMPTY_FILTERS);
    // Display order of the pass tree (view-only, Passes view only — the Aggregate grid sorts itself).
    const [sort, setSort] = React.useState<PassSort>(DEFAULT_SORT);
    const allGroups = React.useMemo(() => groupSummaries(profiling), [profiling]);
    const visibleGroups = React.useMemo(
        () => filterGroups(allGroups, profiling, traces, filters),
        [allGroups, profiling, traces, filters]
    );
    // Clicking an Aggregate row toggles that node's source in the source filter.
    const toggleSourceFilter = React.useCallback((source: string) => {
        setFilters(f => ({
            ...f,
            sources: f.sources.includes(source) ? f.sources.filter(s => s !== source) : [...f.sources, source],
        }));
    }, []);
    // Clicking a Triggered-by row toggles that event (kind + name) in the event filter.
    const toggleEventFilter = React.useCallback((event: EventOption) => {
        setFilters(f => {
            const key = eventOptionKey(event);
            const exists = f.events.some(e => eventOptionKey(e) === key);
            return { ...f, events: exists ? f.events.filter(e => eventOptionKey(e) !== key) : [...f.events, event] };
        });
    }, []);
    const activeEventKeys = React.useMemo(() => new Set(filters.events.map(eventOptionKey)), [filters.events]);
    const sourceHighlight = React.useMemo(
        () => (filters.sources.length ? new Set(filters.sources) : null),
        [filters.sources]
    );
    // Template renders matching the template/source filter values, highlighted in the pass tree.
    const highlightedCycleIds = React.useMemo(() => matchingCycleIds(profiling, filters), [profiling, filters]);

    // Download everything the tab currently shows (filters applied) as re-analyzable raw JSON.
    const handleExport = React.useCallback(() => {
        const now = new Date();
        downloadJson(
            `profiling-${format(now, "yyyyMMddHHmmss")}.json`,
            buildProfilingExport(profiling, traces, filters, { exportedAt: now.toISOString() })
        );
    }, [profiling, traces, filters]);

    // Clear a selection whose pass was filtered out, so the detail pane never shows a hidden pass.
    React.useEffect(() => {
        if (selectedCycle && !visibleGroups.some(g => g.rootCycleId === selectedCycle.groupId)) {
            dispatch(selectCycle(null));
        }
    }, [visibleGroups, selectedCycle, dispatch]);

    // The batch/node selection for the selected cycle's detail stack (timeline -> flame -> detail).
    const { activeBatch, selectedNodeId, selectedNode, selectBatch, selectNode } = useBatchSelection(selectedCycle);

    // Timeline scope: the selected cycle's own batches, or every retained render of its template.
    const [timelineScope, setTimelineScope] = React.useState<TimelineScope>("pass");
    const timelineBatches = React.useMemo(() => {
        if (!selectedCycle) return [];
        return timelineScope === "pass"
            ? cycleBatches(selectedCycle)
            : templateBatches(profiling, selectedCycle.fileName);
    }, [selectedCycle, timelineScope, profiling]);
    // Template scope shows other cycles' batches — clicking one navigates to that cycle too.
    const onTimelineSelectBatch = React.useCallback((b: ProfileData) => {
        if (b.cycleId !== selectedCycleId) dispatch(selectCycle(b.cycleId));
        selectBatch(b);
    }, [selectedCycleId, dispatch, selectBatch]);

    // The toggle persists in localStorage but the cookie can be cleared independently — re-sync it
    // so the reload hint below can always be resolved by actually reloading.
    React.useEffect(() => { setProfilingBuildCookie(reactRenderEnabled); }, [reactRenderEnabled]);

    // Toggling "React render timing" switches builds via cookie, which only takes effect on the
    // next page load. The dev server needs no hint — React's development build already profiles.
    const reloadHint =
        reactRenderEnabled && !isProfilingBuild && !import.meta.env.DEV
            ? "Reload the page to load the React profiling build."
            : !reactRenderEnabled && isProfilingBuild
                ? "Reload the page to return to the standard (faster) build."
                : null;

    return (
        <div className="profiling-layout">
            {/* Toolbar + filter bar share the grid's header row; the body row below gets the rest. */}
            <div className="profiling-header">
                <div className="profiling-toolbar">
                    <span title="Capture render passes (parser, callback and React timings) as you interact with the canvas. Also enables event tracing for pass↔event correlation.">
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={(_e, v) => dispatch(setProfilingEnabled(v))} />}
                            label="Enable profiling"
                        />
                    </span>
                    <span title="Also profiles each custom component via React.Profiler. Toggling this serves the React profiling build (via cookie) — reload the page for the switch to take effect.">
                        <FormControlLabel
                            control={<Switch checked={reactRenderEnabled} disabled={!enabled} onChange={(_e, v) => dispatch(setReactRenderEnabled(v))} />}
                            label="React render timing"
                        />
                    </span>
                    {reloadHint && <span className="profiling-reload-hint">{reloadHint}</span>}

                    <span className="toolbar-spacer" />

                    <span className="cycle-count">{groupCount} pass{groupCount === 1 ? "" : "es"}</span>
                    <div className="view-toggle">
                        <Button size="small" variant={viewMode === "passes" ? "contained" : "outlined"} title="Chronological render passes: pass tree, timeline, flame graph and node detail" onClick={() => setViewMode("passes")}>Passes</Button>
                        <Button size="small" variant={viewMode === "aggregate" ? "contained" : "outlined"} title="Per-node hotspots aggregated by source location across every retained pass" onClick={() => setViewMode("aggregate")}>Aggregate</Button>
                        <Button
                            size="small"
                            variant={viewMode === "compare" ? "contained" : "outlined"}
                            disabled={groupCount === 0}
                            title="Two render cycles side by side with per-metric diffs"
                            onClick={() => {
                                // Seed side A from the tree selection, but never clobber explicit A/B picks.
                                if (!compare.a && !compare.b && selectedCycleId) setCompare(c => ({ ...c, a: selectedCycleId }));
                                setViewMode("compare");
                            }}
                        >
                            Compare
                        </Button>
                    </div>
                    <Button size="small" variant="outlined" disabled={visibleGroups.length === 0} title="Download the passes and aggregate rows matching the current filters as JSON (raw node-level timings included)" onClick={handleExport}>Export</Button>
                    <Button size="small" variant="contained" color="secondary" disabled={groupCount === 0} title="Discard all captured passes. The Event Log is kept; events from before the clear are no longer attributed to new passes." onClick={() => dispatch(clearProfiling())}>Clear</Button>
                </div>

                {/* Compare works on two explicit cycles — the filter bar doesn't apply there. */}
                {viewMode !== "compare" && (groupCount > 0 || hasActiveFilters(filters)) && (
                    <ProfilingFilters filters={filters} onChange={setFilters} groups={allGroups} shownCount={visibleGroups.length} />
                )}
            </div>

            {viewMode === "compare" ? (
                <CompareView cycleIds={compare} onChange={setCompareSide} />
            ) : viewMode === "aggregate" ? (
                <AggregateView filters={filters} onToggleSource={toggleSourceFilter} />
            ) : (
                // Resizable panels: list | detail (horizontal); within detail, timeline / flame-area
                // (vertical); within flame-area, flame graph | node detail (horizontal). Uses a custom
                // pointer-capture splitter (ResizeSplit) so dragging works inside the pop-out window.
                // The split structure stays mounted; only pane content is conditional.
                <ResizeSplit className="profiling-body" direction="horizontal" initialSizes={[20, 80]} minSizes={[220, 360]} gutterSize={8}>
                    <div className="cycle-list">
                        <PassSortBar sort={sort} onChange={setSort} />
                        <div className="cycle-scroll">
                            <CycleTree groups={visibleGroups} highlightedCycleIds={highlightedCycleIds} sort={sort} onSelectForComparison={selectForComparison} />
                        </div>
                    </div>

                    <div className="detail-column" style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        <TriggeredBy selectedCycle={selectedCycle} onToggleEvent={toggleEventFilter} activeEventKeys={activeEventKeys} />
                        <ResizeSplit className="detail-split" direction="vertical" initialSizes={[25, 75]} minSizes={[70, 200]}>
                            <div className="timeline-pane">
                                {selectedCycle && (
                                    <ProfileTimeline
                                        batches={timelineBatches}
                                        selectedBatchKey={activeBatch ? batchKey(activeBatch) : null}
                                        onSelectBatch={onTimelineSelectBatch}
                                        viewKey={timelineScope === "pass" ? selectedCycle.cycleId : `template:${selectedCycle.fileName}`}
                                        scope={timelineScope}
                                        onScopeChange={setTimelineScope}
                                    />
                                )}
                            </div>
                            <ResizeSplit className="flame-and-detail" direction="horizontal" initialSizes={[66, 34]} minSizes={[260, 220]}>
                                <div className="flame-pane">
                                    {!selectedCycle && <div className="cycle-detail-empty">Select a render pass on the left.</div>}
                                    {selectedCycle && (activeBatch
                                        ? <FlameGraph batch={activeBatch} selectedNodeId={selectedNodeId} onSelectNode={selectNode} highlightSources={sourceHighlight} />
                                        : <div className="cycle-detail-empty">This pass produced no timing nodes.</div>)}
                                </div>
                                <div className="detail-pane">
                                    {selectedCycle && activeBatch && <NodeDetail batch={activeBatch} node={selectedNode} />}
                                </div>
                            </ResizeSplit>
                        </ResizeSplit>
                    </div>
                </ResizeSplit>
            )}
        </div>
    );
}

export default React.memo(Profiling);
